<script setup lang="ts">
/* The product mark, with the accent colours swapped so the admin tool
   never looks like the customer app at a glance. */
import { appConfig } from '@/config/app.config'

withDefaults(
  defineProps<{
    /** Hide the wordmark — used by the collapsed sidebar and the loader. */
    markOnly?: boolean
    size?: number
  }>(),
  { markOnly: false, size: 32 },
)
</script>

<template>
  <span class="logo">
    <svg
      viewBox="0 0 32 32"
      :width="size"
      :height="size"
      class="mark"
      role="img"
      :aria-label="markOnly ? appConfig.name : undefined"
      :aria-hidden="markOnly ? undefined : true"
    >
      <rect width="32" height="32" rx="7" class="markBg" />
      <g stroke-width="2.4" stroke-linecap="round" fill="none" class="markWaves">
        <path d="M4 19c3.5 0 3.5-4 7-4s3.5 4 7 4 3.5-4 7-4" />
        <path d="M4 25c3.5 0 3.5-4 7-4s3.5 4 7 4 3.5-4 7-4" opacity=".5" />
      </g>
      <circle cx="19" cy="10" r="3" class="markDot" />
    </svg>

    <span v-if="!markOnly" class="text">
      <span class="wordmark">{{ appConfig.shortName }}</span>
      <span class="tag">Admin</span>
    </span>
  </span>
</template>

<style scoped>
.logo {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2xs);
  min-width: 0;
  color: var(--color-heading);
  text-decoration: none;
}

.mark {
  flex: none;
  border-radius: var(--radius-sm);
}

.markBg {
  fill: var(--rv-navy-950);
}

/* Sand where the app is teal — the one visual tell that this is the
   internal tool, carried through to the favicon. */
.markWaves {
  stroke: var(--rv-sand-500);
}

.markDot {
  fill: var(--rv-teal-400);
}

.text {
  display: flex;
  align-items: baseline;
  gap: var(--space-3xs);
  min-width: 0;
}

.wordmark {
  font-family: var(--font-display);
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-tight);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag {
  flex: none;
  padding: 0.1em var(--space-3xs);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--color-accent-2);
  background-color: var(--color-accent-2-soft);
  border-radius: var(--radius-xs);
}
</style>
